import React from 'react';
import {useTranslation} from 'react-i18next';
import get from 'lodash.get';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {Checkbox, GearIcon, Heading, TextInput} from '@taopix/taopix-design-system';
import {useExperience} from '../../Experience/ExperienceProvider';
import classNames from 'classnames';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';

export type ExperiencePaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

const ExperienceSettings = ({sectionKey, item}: ExperiencePaletteProps) => {
  const {t} = useTranslation();
  const {updateExperience, experience} = useExperience();

  const onChangeExperience = (isChecked: boolean, event: React.ChangeEvent<HTMLInputElement>) => {
    const {name} = event.target;
    const value = event.target.hasOwnProperty('checked') ? isChecked : event.target.value;
    const parts = name.split('.');
    const key = parts.pop();
    const updates = {...experience};
    // update the tree
    get(updates, parts.join('.'))[key] = value;

    updateExperience(updates);
  };

  const deCamelCase = (str: string): string => {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  };

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames('flex flex-col pb-lg space-y-sm overflow-y-auto px-md flex-1');

  const renderItem = (item: Array<any>, prefix: string): React.ReactElement | null => {
    if (['inactivityDuration'].includes(item[0])) return null;
    switch (typeof item[1]) {
      case 'object':
        return (
          <div key={prefix.length ? `${prefix}.${item[0]}` : `${item[0]}`}>
            <Heading
              level={3}
              className={'pb-2 capitalize'}
              key={prefix.length ? `${prefix}.${item[0]}` : `${item[0]}`}
            >
              {deCamelCase(item[0])}
            </Heading>
            <div
              className="ml-5"
              key={prefix.length ? `container-${prefix}.${item[0]}` : `container-${item[0]}`}
            >
              {Object.entries(item[1]).map(subItem => {
                return renderItem(subItem, prefix.length ? `${prefix}.${item[0]}` : `${item[0]}`);
              })}
            </div>
          </div>
        );
      case 'boolean':
        return (
          <Checkbox
            label={deCamelCase(item[0])}
            id={`${prefix}.${item[0]}`}
            name={`${prefix}.${item[0]}`}
            defaultChecked={item[1]}
            onChange={onChangeExperience}
            className={'mb-2 capitalize'}
            key={`${prefix}.${item[0]}`}
          />
        );
      default:
        return (
          <TextInput
            label={deCamelCase(item[0])}
            id={`${prefix}.${item[0]}`}
            name={`${prefix}.${item[0]}`}
            defaultValue={item[1]}
            onChange={e => onChangeExperience(false, e)}
            className={'mb-2 capitalize w-full'}
            key={`${prefix}.${item[0]}`}
          />
        );
    }
  };

  return (
    <ToolPaletteSection
      subSection
      buttonClasses={sectionButtonClasses}
      sectionKey={sectionKey}
      smallScreenPanelHeight="full"
      subSectionPanelType="slide-in"
      title={t('str_LabelExperience')}
      buttonIcon={<GearIcon />}
      id="experiencePalette"
      buttonLayout={'horizontal'}
    >
      <div className={wrapperClasses}>
        {Object.entries(experience).map(item => {
          return <div key={`top-level-${item[0]}`}>{renderItem(item, '')}</div>;
        })}
      </div>
    </ToolPaletteSection>
  );
};

export default ExperienceSettings;
